import React from 'react';
import { motion } from 'framer-motion';
import {
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CreditCardIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../../contexts/LanguageContext';

const OverviewCards: React.FC = () => {
  const { t } = useLanguage();

  const cards = [
    {
      title: t('dashboard.total_balance'),
      value: '1,247,500 ر.س',
      change: '+12.5%',
      changeType: 'increase',
      icon: BanknotesIcon,
      color: 'from-blue-500 to-blue-600',
      description: t('dashboard.compared_last_month')
    },
    {
      title: t('dashboard.monthly_revenue'),
      value: '485,200 ر.س',
      change: '+8.2%',
      changeType: 'increase',
      icon: ArrowTrendingUpIcon,
      color: 'from-green-500 to-green-600',
      description: t('dashboard.revenue_growth')
    },
    {
      title: t('dashboard.monthly_expenses'),
      value: '312,800 ر.س',
      change: '-3.1%',
      changeType: 'decrease',
      icon: ArrowTrendingDownIcon,
      color: 'from-red-500 to-red-600',
      description: t('dashboard.expense_reduction')
    },
    {
      title: t('dashboard.active_cards'),
      value: '24',
      change: '+4',
      changeType: 'increase',
      icon: CreditCardIcon,
      color: 'from-purple-500 to-purple-600',
      description: t('dashboard.smart_cards_active')
    },
    {
      title: t('dashboard.partnerships_savings'),
      value: '47,300 ر.س',
      change: '+18.7%',
      changeType: 'increase',
      icon: UserGroupIcon,
      color: 'from-indigo-500 to-indigo-600',
      description: t('dashboard.partnership_benefits')
    },
    {
      title: t('dashboard.financing_available'),
      value: '850,000 ر.س',
      change: 'متاح',
      changeType: 'neutral',
      icon: ChartBarIcon,
      color: 'from-yellow-500 to-yellow-600',
      description: t('dashboard.instant_financing')
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      {cards.map((card, index) => (
        <motion.div
          key={card.title}
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -5 }}
          className="relative group"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
            {/* Background gradient */}
            <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${card.color} opacity-10 rounded-2xl transform rotate-12 group-hover:rotate-6 transition-transform duration-300`}></div>
            
            {/* Icon */}
            <div className={`w-12 h-12 bg-gradient-to-r ${card.color} rounded-xl p-3 mb-4 shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
              <card.icon className="w-6 h-6 text-white" />
            </div>

            {/* Content */}
            <div className="relative z-10">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                {card.title}
              </h3>
              <div className="flex items-baseline justify-between">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {card.value}
                </p>
                <div className={`flex items-center text-sm font-semibold ${
                  card.changeType === 'increase' 
                    ? 'text-green-600 dark:text-green-400' 
                    : card.changeType === 'decrease'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`}>
                  {card.changeType === 'increase' && <ArrowUpIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />}
                  {card.changeType === 'decrease' && <ArrowDownIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />}
                  {card.changeType === 'neutral' && <ExclamationTriangleIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />}
                  {card.change}
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {card.description}
              </p>
            </div>

            {/* Hover effect */}
            <div className={`absolute inset-0 bg-gradient-to-r ${card.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`}></div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default OverviewCards;
